import React, { useState } from"react";
import { useLocation, useNavigate } from"react-router-dom";

const MockResultPage = ({ mode = "learner" }) => {
 const navigate = useNavigate();
 const location = useLocation();

 const isTenantMode = mode === "tenant";
 // NextGen Builder / TSG Nexus embeds this page under
 // /nextgen-builder/mock-interview — navigation must stay inside
 // /nextgen-builder/*, never the college/tenant /student/* routes.
 const isNextGenBuilderRoute = window.location.pathname.startsWith('/nextgen-builder');

 const {
 topic,
 correct,
 wrong,
 score,
 time,
 total,
 user_id,
 answers,
 questions = [],
 } = location.state || {};

 const [showReview, setShowReview] = useState(false);

 const getFeedback = () => {
 const normalizedTopic = topic.replace(/-/g,"");

 if (score >= 90) {
 return (
 <>
 🔥 Excellent! You scored {score}% on the <b>{normalizedTopic}</b>{""}
 topic.
 <br />
 Your strong grasp of key concepts shows great preparation.
 <br />
 Keep practicing to maintain this high level.
 <br />
 Consider exploring advanced topics to deepen your knowledge.
 <br />
 Your timing was impressive — great job managing it.
 <br />
 You're ready for real-world applications and interviews.
 <br />
 Keep up the fantastic work and confidence!
 </>
 );
 }

 if (score >= 75) {
 return (
 <>
 ✅ Good job scoring {score}% in <b>{normalizedTopic}</b>!<br />
 You have a solid foundation but can improve in some areas.
 <br />
 Review the questions you missed and understand why.
 <br />
 Practice those weak points with targeted exercises.
 <br />
 Try to improve your speed without sacrificing accuracy.
 <br />
 Consistent study will help you reach excellence.
 <br />
 Keep pushing — you’re on the right path!
 </>
 );
 }

 if (score >= 50) {
 return (
 <>
 ⚠️ Fair attempt with {score}% on <b>{normalizedTopic}</b>.<br />
 You’ve understood some concepts but need more practice.
 <br />
 Revisit key topics and clarify any doubts.
 <br />
 Focus on the questions you answered incorrectly.
 <br />
 Consider using flashcards or quizzes to reinforce learning.
 <br />
 Improving your fundamentals will boost your confidence.
 <br />
 Stay persistent and review regularly to improve!
 </>
 );
 }

 return (
 <>
 ❌ You scored {score}% on <b>{normalizedTopic}</b>.<br />
 It’s important to revisit the basics and strengthen your foundation.
 <br />
 Review each incorrect answer carefully to understand mistakes.
 <br />
 Consider breaking down complex topics into smaller parts.
 <br />
 Regular practice will help build accuracy and speed.
 <br />
 Don’t get discouraged — improvement comes with effort.
 <br />
 Try retaking the test after additional study sessions.
 </>
 );
 };

 if (!questions.length) {
 return (
 <div className="text-white p-6">No data available for this test.</div>
 );
 }

 return (
 <div className="bg-bgMain text-body min-h-screen p-8 md:p-12 relative overflow-hidden font-sans">
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
 
 {!showReview ? (
 <div className="bg-bgSection p-10 md:p-12 rounded-[48px] shadow-2xl text-center max-w-2xl mx-auto border border-primary/30 relative z-10 overflow-hidden"
 style={{ boxShadow:"0 25px 60px rgba(0, 0, 0, 0.4)" }}>
 <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
 
 <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-primary/20">
 <span className="text-5xl">🏆</span>
 </div>

 <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-6 uppercase tracking-tighter">
 Test Completed!
 </h1>
 
 <div className="space-y-2 mb-8">
 <p className="text-lg font-bold text-body">
 Topic: <span className="capitalize text-primary">{topic.replace(/-/g,"")}</span>
 </p>
 <div className="flex justify-center gap-6 text-sm font-black uppercase tracking-widest text-muted">
 <span>⏱️ {time}</span>
 <span>📊 {total} Questions</span>
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4 mb-10">
 <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-3xl">
 <p className="text-[10px] font-black text-green-400 uppercase tracking-widest mb-1">Correct</p>
 <p className="text-2xl font-black text-heading">{correct}</p>
 </div>
 <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-3xl">
 <p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Wrong</p>
 <p className="text-2xl font-black text-heading">{wrong}</p>
 </div>
 </div>

 <div className="mb-10">
 <p className="text-sm font-black text-primary uppercase tracking-widest mb-2">Final Performance Score</p>
 <div className="text-6xl font-black text-heading tracking-tighter">{score}%</div>
 </div>

 <div className="bg-bgSoft p-8 rounded-3xl border border-accentGray/30 text-left mb-10">
 <p className="text-body/90 italic leading-relaxed font-medium">
 {getFeedback()}
 </p>
 </div>

 <div className="flex flex-col sm:flex-row gap-4">
 <button
 onClick={() => setShowReview(true)}
 className="flex-1 bg-bgMain text-primary border border-primary/30 px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-primary hover:text-white transition-all shadow-lg"
 >
 Review Answers
 </button>
              <button
                onClick={() => navigate(isNextGenBuilderRoute ? "/nextgen-builder/mock-interview/arithmetic" : (isTenantMode ? "/student/mockinterview/arithmetic" : "/mockinterview/arithmetic"))}
                className="flex-1 btn-primary-gradient px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all"
              >
                Retake Test
              </button>
            </div>
          </div>
        ) : (
          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h1 className="text-3xl font-black text-heading uppercase tracking-tighter">
                Performance <span className="text-primary">Review</span>
              </h1>
              <button
                onClick={() => navigate(isNextGenBuilderRoute ? "/nextgen-builder/mock-interview" : (isTenantMode ? "/student/mockinterviewhome" : "/mockinterviewhome"))}
                className="bg-bgSection text-primary border border-primary/30 px-6 py-2 rounded-full font-black uppercase text-[10px] tracking-widest hover:bg-primary hover:text-white transition-all shadow-lg"
              >
                Back to Dashboard
              </button>
            </div>

 <div className="grid gap-8">
 {questions.map((q, idx) => {
 const userAnswer = answers?.[q.title];
 const isCorrect = userAnswer === q.correct_option;

 return (
 <div
 key={idx}
 className="bg-bgSection p-8 rounded-[32px] border border-accentGray/30 shadow-2xl relative overflow-hidden group"
 >
 <div className="flex justify-between items-start mb-6">
 <div className="text-primary font-black uppercase text-xs tracking-widest">
 Question {idx + 1}
 </div>
 {isCorrect ? (
 <span className="px-4 py-1.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/30 text-[10px] font-black uppercase tracking-widest">Correct</span>
 ) : (
 <span className="px-4 py-1.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 text-[10px] font-black uppercase tracking-widest">Incorrect</span>
 )}
 </div>
 
 <p className="text-xl font-bold text-heading mb-8 leading-relaxed tracking-tight">{q.question_text}</p>

 <div className="grid gap-3">
 {["a","b","c","d"].map((optKey) => {
 const optionLabel = optKey.toUpperCase();
 const isUserChoice = userAnswer === optionLabel;
 const isCorrectOption = q.correct_option === optionLabel;

 let borderColor ="rgba(255, 255, 255, 0.05)";
 let bgColor ="rgba(2, 6, 23, 0.5)";
 let textColor ="#94A3B8";

 if (isCorrectOption) {
 borderColor ="rgba(16, 185, 129, 0.5)";
 bgColor ="rgba(16, 185, 129, 0.1)";
 textColor ="#4ade80";
 } else if (isUserChoice && !isCorrectOption) {
 borderColor ="rgba(239, 68, 68, 0.5)";
 bgColor ="rgba(239, 68, 68, 0.1)";
 textColor ="#f87171";
 }

 return (
 <div
 key={optKey}
 className="p-4 rounded-2xl border-2 transition-all flex justify-between items-center"
 style={{ borderColor, background: bgColor }}
 >
 <div className="flex items-center gap-4">
 <span className="w-8 h-8 rounded-lg bg-bgSection flex items-center justify-center font-black text-xs" style={{ color: textColor }}>{optionLabel}</span>
 <span className="font-medium" style={{ color: textColor }}>{q[`option_${optKey}`]}</span>
 </div>
 {isUserChoice && <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Your Choice</span>}
 </div>
 );
 })}
 </div>
 </div>
 );
 })}
 </div>

 <div className="mt-16 text-center">
 <button
 onClick={() => setShowReview(false)}
 className="bg-bgMain text-muted border border-accentGray/50 px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:text-white transition-all shadow-xl"
 >
 Back to Summary
 </button>
 </div>
 </div>
 )}
 </div>
 );
};

export default MockResultPage;



